import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import { allArticles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  const relatedArticles = article
    ? allArticles
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-2xl text-foreground-400" />
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '寸止挑战训练中心',
    },
    publisher: {
      '@type': 'Organization',
      name: '寸止挑战训练中心',
    },
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: article.categoryName,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEOMeta
        title={`${article.title} - 寸止挑战训练中心`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://cunzhitiaozhan.com/article/${slug}`}
        ogType="article"
      />

      <Navbar />

      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>

            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div className="relative h-64 md:h-96 rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-sm prose-p:md:text-base
              prose-strong:text-foreground-900 prose-strong:font-semibold
              prose-li:text-foreground-700 prose-li:text-sm prose-li:leading-relaxed
              [&_h2]:relative [&_h2]:pl-5 [&_h2]:before:content-[''] [&_h2]:before:absolute [&_h2]:before:left-0 [&_h2]:before:top-1 [&_h2]:before:bottom-1 [&_h2]:before:w-1 [&_h2]:before:bg-primary-500 [&_h2]:before:rounded-full"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享文章：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-sm" />
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm" />
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm" />
                </button>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <i className="ri-arrow-left-line" /> 返回{article.categoryName}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-colors"
                >
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      title={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors text-sm mb-1 line-clamp-2">
                      {ra.title}
                    </h3>
                    <span className="text-xs text-foreground-400">{ra.readTime} 分钟阅读</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}